import {
  Rule,
  FeaturesState,
  makeWeeklyPatternRule,
  makeRotatingPatternRule,
  makeAbsoluteRangeRule,
  makeDaysOfTheWeek,
  makeDateRecord,
  makeDateTimeRecord,
  RequiredEffectivePeriodSchema,
  makeFeature,
  FeatureState,
  Features,
} from "@visioni/schedules";
import { AlarmSchedule } from "./alarm-schedules/types";
import type {
  AlarmResponse,
  AlarmResponseToggle,
  AlarmRoutine,
  AlarmExceptions,
  AlarmTimezone,
  AlarmOverride,
  AlarmSilence,
} from "./schema";

function timetableFeatureStateOf(responseToggle: AlarmResponseToggle): FeatureState {
  switch (responseToggle) {
    case "off":
      return "disarmed" as const;
    case "on":
      return "armed" as const;
    default: {
      const exhaustive: never = responseToggle;
      throw new Error(`Unhandled response toggle: ${String(exhaustive)}`);
    }
  }
}

function timetableFeaturesStateOf(response: AlarmResponse): FeaturesState {
  const features: FeaturesState = {};

  for (const [feature, toggle] of Object.entries(response)) {
    features[feature] = timetableFeatureStateOf(toggle);
  }

  return features;
}

function timetableFeaturesOf(response: AlarmResponse): Features {
  const features: Features = {};

  for (const feature of Object.keys(response)) {
    features[feature] = makeFeature({
      default_state: timetableFeatureStateOf("off"),
      allowed_states: [timetableFeatureStateOf("on"), timetableFeatureStateOf("off")],
    });
  }

  // publishAlarm has no corresponding AlarmResponse toggle — it always mirrors a
  // rule's own armed/disarmed intent, so both states stay available unconditionally.
  features["publishAlarm"] = makeFeature({ default_state: "disarmed", allowed_states: ["armed", "disarmed"] });

  return features;
}

/**
 * Compute a full Schedule from the Alarm domain model.
 * - Weekly/Rotating routine produces repeating rules with the "armed" response.
 * - Exceptions produce absolute override rules with either "armed" or "disarmed" states.
 * - Override produces the final absolute override rule (applied last in the stack).
 * - Default feature states represent the disarmed baseline (from alarmResponseFeatures).
 */
export function computeScheduleForAlarmSettings(
  now: string,
  timezone: AlarmTimezone,
  response: AlarmResponse,
  routine: AlarmRoutine,
  exceptions: AlarmExceptions,
  override: AlarmOverride | null = null,
  silence: AlarmSilence | null = null,
): AlarmSchedule {
  const features = timetableFeaturesOf(response);
  // monitoring and publishAlarm are not user toggles: in an armed window both
  // always arm, mirroring the rule's own armed/disarmed intent. Whether
  // monitoring actually counts is decided at the edge by composing the asset's
  // Monitoring Contract Windows at read time — it is never baked in here, so the
  // stored schedule stays a pure function of the user's settings (ADR-0071).
  const featuresArmedState: FeaturesState = {
    ...timetableFeaturesStateOf(response),
    monitoring: "armed",
    publishAlarm: "armed",
  };
  const featuresDisarmedState = Object.entries(features).reduce<FeaturesState>((acc, [key, val]) => {
    acc[key] = val.default_state;
    return acc;
  }, {});

  const rules: Rule[] = [];

  switch (routine.kind) {
    case "weekly":
      routine.windows.forEach((w, idx) => {
        rules.push(
          makeWeeklyPatternRule({
            id: `weekly:${String(idx)}`,
            name: w.name ?? `Weekly ${w.days.join(",")}`,
            features: featuresArmedState,
            weekly_pattern: {
              effective_period: { start: null, end: null },
              days: makeDaysOfTheWeek(w.days),
              // TODO: make schedules lib more composable
              time_ranges: w.timeRanges,
            },
          }),
        );
      });
      break;
    case "rotating":
      rules.push(
        makeRotatingPatternRule({
          id: "rotating:0",
          name: "Rotating Routine",
          features: featuresArmedState,
          rotating_pattern: {
            effective_period: { start: null, end: null },
            start: makeDateRecord(routine.start.year, routine.start.month, routine.start.day),
            pattern: routine.pattern,
          },
        }),
      );
      break;
    default: {
      const exhaustive: never = routine;
      throw new Error(`Unhandled routine kind: ${String(exhaustive)}`);
    }
  }

  for (const ex of exceptions) {
    rules.push(
      makeAbsoluteRangeRule({
        id: `exception:${ex.id}`,
        name: ex.reason,
        features: ex.action === "armed" ? featuresArmedState : featuresDisarmedState,
        absolute_range: {
          effective_period: RequiredEffectivePeriodSchema.parse(ex.period),
        },
      }),
    );
  }

  // Silence is applied after routine/exceptions but before override.
  // It forces sirens and strobes off for the duration.
  if (silence) {
    const silenceFeatures: FeaturesState = { ...featuresArmedState };
    silenceFeatures["sirens"] = featuresDisarmedState["sirens"] ?? "off";
    silenceFeatures["strobes"] = featuresDisarmedState["strobes"] ?? "off";
    silenceFeatures["publishAlarm"] = featuresDisarmedState["publishAlarm"] ?? "disarmed";

    rules.push(
      makeAbsoluteRangeRule({
        id: `silence:${silence.id}`,
        name: "Silence",
        features: silenceFeatures,
        absolute_range: {
          effective_period: RequiredEffectivePeriodSchema.parse(silence.period),
        },
      }),
    );
  }

  // Override is applied as the final rule in the stack
  if (override) {
    rules.push(
      makeAbsoluteRangeRule({
        id: `override:${override.id}`,
        name: override.reason,
        features: override.action === "armed" ? featuresArmedState : featuresDisarmedState,
        absolute_range: {
          effective_period: {
            start: makeDateTimeRecord(
              override.period.start.year,
              override.period.start.month,
              override.period.start.day,
              override.period.start.hour,
              override.period.start.minute,
            ),
            end:
              override.period.end ?
                makeDateTimeRecord(
                  override.period.end.year,
                  override.period.end.month,
                  override.period.end.day,
                  override.period.end.hour,
                  override.period.end.minute,
                )
              : null,
          },
        },
      }),
    );
  }

  return AlarmSchedule.parse({
    schema_version: "1",
    metadata: {
      updated_at_utc: now,
      suggested_input_time_precision_in_minutes: 10,
      expected_time_precision_in_minutes: 10,
      timezone,
    },
    features,
    rules,
  });
}
